import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hvApi } from '../lib/api';
import { ChevronDown, User, LogOut, CreditCard, Briefcase, Palette, Bell, Settings, Star, ArrowRight, Clock, Menu, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

export default function HVNav({ unreadReviews = 0 }) {
  const { user, logout } = useAuth('hv_user');
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const userMenuRef = useRef(null);
  const bellRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handle = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (notifLoading) return;
    setNotifLoading(true);
    try {
      const res = await hvApi.getAllReviews({ answered: false, limit: 6, skip: 0 });
      setNotifications(res.data?.reviews || []);
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBellClick = () => {
    const next = !bellOpen;
    setBellOpen(next);
    if (next) fetchNotifications();
    setSettingsOpen(false);
    setUserMenuOpen(false);
  };

  const settingsItems = [
    { label: 'Unternehmenseinstellungen', icon: Briefcase, href: '/app/settings/company' },
    { label: 'Design', icon: Palette, href: '/app/settings/design' },
    // Ausgeblendet (Feedback 2026-07): Rollenverwaltung, Werbebanner, Signaturdesigner
    { label: 'Billing', icon: CreditCard, href: '/app/settings/billing' },
  ];

  const navLinks = [
    { label: 'Übersicht', href: '/app' },
    { label: 'Links', href: '/app/links' },
    { label: 'Bewertungen', href: '/app/reviews' },
  ];

  const ratingColor = (r) => {
    if (r >= 4) return 'text-green-600';
    if (r === 3) return 'text-yellow-600';
    return 'text-red-500';
  };

  return (
    <header className="bg-background border-b border-border sticky top-0 z-40">
      <div className="container-main flex items-center justify-between h-20">
        {/* Logo + Nav */}
        <div className="flex items-center gap-6">
          <Link to="/app">
            <img src="/logo.png" alt="Immoverita" className="h-16 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`relative text-sm px-3 py-1.5 rounded-md transition-colors ${
                  isActive(link.href)
                    ? 'font-medium bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {link.label}
                {link.href === '/app/reviews' && unreadReviews > 0 && (
                  <span className="absolute top-1 right-1 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                  </span>
                )}
              </Link>
            ))}
            {/* Einstellungen-Dropdown entfernt (Atarim-13): erreichbar über das User-Menü rechts. */}
          </nav>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">

          {/* Notification Bell */}
          <div className="relative" ref={bellRef}>
            <button
              onClick={handleBellClick}
              className={`relative p-2 rounded-md hover:bg-muted transition-colors ${
                unreadReviews > 0 ? 'text-orange-500' : 'text-muted-foreground'
              }`}
              title="Benachrichtigungen"
            >
              <Bell className="w-4 h-4" />
              {unreadReviews > 0 && (
                <span className="absolute top-1 right-1 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
                </span>
              )}
            </button>

            {bellOpen && (
              <div className="absolute top-full right-0 mt-1 w-80 bg-background border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-orange-500" />
                    <span className="text-sm font-semibold">Unbeantwortete Bewertungen</span>
                    {unreadReviews > 0 && (
                      <span className="bg-orange-100 text-orange-700 text-xs font-medium px-1.5 py-0.5 rounded-full">
                        {unreadReviews}
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="max-h-80 overflow-y-auto">
                  {notifLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Alle Bewertungen beantwortet</p>
                    </div>
                  ) : (
                    notifications.map((review) => (
                      <button
                        key={review.id}
                        onClick={() => {
                          setBellOpen(false);
                          navigate(`/app/links/${review.review_link_id}`);
                        }}
                        className="w-full flex items-start gap-3 px-4 py-3 hover:bg-muted/60 transition-colors text-left border-b border-border/50 last:border-0"
                      >
                        {/* Stars */}
                        <div className="shrink-0 flex items-center gap-0.5 mt-0.5">
                          {[1,2,3,4,5].map(i => (
                            <Star
                              key={i}
                              className={`w-3 h-3 ${i <= review.overall_rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground/30'}`}
                            />
                          ))}
                        </div>
                        {/* Text */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">
                            {review.free_text || review.rewritten_text || 'Keine Freitextanmerkung'}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-xs text-muted-foreground truncate">{review.link_name || '—'}</span>
                            {review.createdAt && (
                              <>
                                <span className="text-xs text-muted-foreground">·</span>
                                <Clock className="w-2.5 h-2.5 text-muted-foreground shrink-0" />
                                <span className="text-xs text-muted-foreground shrink-0">
                                  {formatDistanceToNow(new Date(review.createdAt), { addSuffix: true, locale: de })}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* Footer */}
                {unreadReviews > 0 && (
                  <div className="border-t border-border px-4 py-2.5">
                    <Link
                      to="/app/reviews?answered=false"
                      onClick={() => setBellOpen(false)}
                      className="flex items-center justify-between text-xs text-primary hover:underline font-medium"
                    >
                      <span>Alle {unreadReviews} unbeantworteten anzeigen</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Hamburger — mobile only */}
          <button
            onClick={() => setMobileMenuOpen(o => !o)}
            className="md:hidden p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground"
            aria-label="Menü öffnen"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(o => !o)}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
              <span className="text-sm text-muted-foreground hidden sm:block">
                {user?.tenant?.public_profile?.display_name || user?.email}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {userMenuOpen && (
              <div className="absolute top-full right-0 mt-1 w-52 bg-background border border-border rounded-lg shadow-lg py-1 z-50">
                <div className="px-3 py-2 border-b border-border">
                  <p className="text-xs font-medium truncate">{user?.email}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {user?.tenant?.billing?.plan || 'Free'} Plan
                  </p>
                </div>
                <Link
                  to="/app/settings/company"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors"
                >
                  <Settings className="w-4 h-4 text-muted-foreground" />
                  Einstellungen
                </Link>
                <button
                  onClick={() => { logout(); setUserMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-muted transition-colors"
                  data-testid="hv-logout-button"
                >
                  <LogOut className="w-4 h-4" />
                  Abmelden
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu — full-width dropdown, md:hidden */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background border-b border-border shadow-lg z-30 absolute w-full left-0">
          <nav className="container-main py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {link.label}
                {link.href === '/app/reviews' && unreadReviews > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                    {unreadReviews}
                  </span>
                )}
              </Link>
            ))}
            <div className="pt-1 border-t border-border mt-1">
              <p className="px-3 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">Einstellungen</p>
              {settingsItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors ${
                    isActive(item.href)
                      ? 'bg-muted text-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  {item.label}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
